'use client';

import { AlertCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { useRouter } from 'next/navigation';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  menteeSignUpFormSchema,
  menteeSignUpFormDefaultValues,
} from '@/schemas/menteeSignUp';
import { copy } from '@/data/copy';
import {
  countryData,
  mentoringSkillsList,
  timezoneData,
} from '@/data/constants';
import { createMenteeUserSDK } from '@/server/actions/userSDK';
import { Form, FormField, FormRow } from '@/components/ui/form';

import FormInput from '../formElements/FormInput';
import Mail from '../icons/Mail';
import InputPassword from '../formElements/InputPassword';
import InputSubmit from '../formElements/InputSubmit';
import { SelectInputGroup } from '../formElements/SelectInputGroup';
import StateProvinceRenderer from '../formElements/StateProvinceRenderer';
import { Alert, AlertDescription } from '../ui/alert';
import MultiSelect from '../formElements/MultiSelect';
import { submitWelcomeEmail } from '@/server/actions/emailCommunications';
import BackToPreviousPage from '../utils/BackToPreviousPage';

type FieldType = z.infer<typeof menteeSignUpFormSchema>;

export default function MenteeSignUpForm() {
  const router = useRouter();
  const [selectedCountry, setSelectedCountry] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<null | string>(null);

  const form = useForm<FieldType>({
    resolver: zodResolver(menteeSignUpFormSchema),
    defaultValues: menteeSignUpFormDefaultValues,
  });

  const countryValue = useWatch({ control: form.control, name: 'country' });

  useEffect(() => {
    if (countryValue !== '' || countryValue !== selectedCountry) {
      setSelectedCountry(countryValue);
    }
  }, [countryValue, selectedCountry]);

  async function onSubmit(values: FieldType) {
    setLoading(true);

    const { success, error } = await createMenteeUserSDK(values);

    if (success && success.user.verify) {
      await submitWelcomeEmail({
        name: `${values.firstName} ${values.lastName}`,
        email: values.email,
        actionUrl: `${window.location.origin}/dashboard/mentee`,
      });
      router.replace(`/signup/verify?d=${success.user.verify}`);
      return;
    }

    if (error) {
      console.error(error);
      setError(error);
    }

    setLoading(false);
  }

  return (
    <Form {...form}>
      <form
        method="post"
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-8 mt-10 w-full"
      >
        {error && (
          <Alert variant="destructive">
            <AlertCircle />
            <AlertDescription className="text-lg ml-2">
              {error}
            </AlertDescription>
          </Alert>
        )}

        <BackToPreviousPage className="text-md" />

        <FormField
          control={form.control}
          name="mentorshipFocus"
          render={({ field }) => (
            <MultiSelect
              name={field.name}
              onBlur={field.onBlur}
              onChange={field.onChange}
              ref={field.ref}
              id="mentee-signup-form"
              placeholder={copy.common.form.selectMentorshipFocus}
              label={copy.common.form.selectMentorshipFocus}
              selectOptions={mentoringSkillsList}
            />
          )}
        />

        <FormRow>
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormInput
                label={copy.common.form.firstName}
                placeholder={`${copy.common.form.your} ${copy.common.form.firstName}`}
                {...field}
              />
            )}
          />

          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormInput
                label={copy.common.form.lastName}
                placeholder={`${copy.common.form.your} ${copy.common.form.lastName}`}
                {...field}
              />
            )}
          />
        </FormRow>

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormInput
              label={copy.common.form.emailAddress}
              placeholder={`${copy.common.form.your} ${copy.common.form.emailAddress}`}
              autoComplete="username"
              icon={<Mail />}
              {...field}
            />
          )}
        />

        <FormRow>
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <InputPassword
                label={copy.common.form.password}
                placeholder={`${copy.common.form.your} ${copy.common.form.password}`}
                description={copy.common.form.passwordInputDescription}
                hasIcon
                autoComplete="new-password"
                {...field}
              />
            )}
          />
          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <InputPassword
                label={copy.common.form.confirmPassword}
                placeholder={`${copy.common.form.your} ${copy.common.form.confirmPassword}`}
                hasIcon
                autoComplete="new-password"
                {...field}
              />
            )}
          />
        </FormRow>

        <FormRow>
          <FormField
            control={form.control}
            name="country"
            render={({ field }) => (
              <SelectInputGroup
                label={copy.common.form.country}
                placeholder={`${copy.common.form.your} ${copy.common.form.country}`}
                selectOptionGroups={countryData}
                {...field}
              />
            )}
          />

          <FormField
            control={form.control}
            name="stateProvince"
            render={({ field }) => (
              <StateProvinceRenderer
                selectedCountry={selectedCountry}
                field={field}
              />
            )}
          />
        </FormRow>

        <FormRow>
          <FormField
            control={form.control}
            name="city"
            render={({ field }) => (
              <FormInput
                label={copy.common.form.city}
                placeholder={`${copy.common.form.your} ${copy.common.form.city}`}
                {...field}
              />
            )}
          />
          <FormField
            control={form.control}
            name="timezone"
            render={({ field }) => (
              <SelectInputGroup
                label={copy.common.form.preferredTimezone}
                placeholder={copy.common.form.selectATimezone}
                selectOptionGroups={timezoneData}
                {...field}
              />
            )}
          />
        </FormRow>
        <InputSubmit
          hasIcon
          className="uppercase"
          isLoading={loading}
          disabled={loading}
        >
          {loading ? copy.common.form.signingUp : copy.common.form.signUp}
        </InputSubmit>
      </form>
    </Form>
  );
}
